// Robust Excel/CSV date parsing helpers - TR-first
// Fixes: When Excel cells have US format codes (m/d/yyyy), XLSX would return
// pre-formatted "7/3/2026" strings which our TR parser then misreads.
// Solution: BEFORE sheet_to_json, rewrite date cells' .w using the underlying
// numeric serial via XLSX.SSF, so we always see "dd.mm.yyyy" regardless of
// the original Excel display format.

import * as XLSX from "xlsx";

// Overwrite .w for all cells whose format code is a date, using a canonical
// TR "dd.mm.yyyy" format derived from the underlying Excel serial number.
export function normalizeSheetDates(ws) {
  if (!ws || !ws["!ref"]) return ws;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[addr];
      if (!cell || cell.t !== "n") continue;
      const z = cell.z || "";
      let isDate = false;
      try { isDate = XLSX.SSF && typeof XLSX.SSF.is_date === "function" && XLSX.SSF.is_date(z); }
      catch (_) { isDate = false; }
      if (!isDate) continue;
      try {
        const dc = XLSX.SSF.parse_date_code(cell.v);
        if (dc && dc.y && dc.m && dc.d) {
          const y = dc.y;
          const m = String(dc.m).padStart(2, "0");
          const d = String(dc.d).padStart(2, "0");
          const iso = `${d}.${m}.${y}`;
          // Convert numeric date cell → string cell so downstream sheet_to_json
          // (raw:false or raw:true) yields TR "dd.mm.yyyy" instead of Excel serial.
          cell.t = "s";
          cell.v = iso;
          cell.w = iso;
          cell.z = "dd.mm.yyyy";
        }
      } catch (_) { /* ignore */ }
    }
  }
  return ws;
}

const expandYear = (y) => {
  const n = parseInt(y, 10);
  if (isNaN(n)) return null;
  if (n < 100) return n < 50 ? 2000 + n : 1900 + n;
  return n;
};

// TR-first date parser. Accepts:
//   - Date instance          (uses UTC methods to avoid timezone drift)
//   - Number (Excel serial)  (rare; kept as safety)
//   - ISO string YYYY-MM-DD
//   - dd.mm.yyyy / dd-mm-yyyy
//   - dd/mm/yyyy (TR default when both <=12; disambiguates when one >12)
export function parseTRDate(v) {
  if (v === null || v === undefined || v === "") return "";

  // Excel Date instance (from cellDates:true) — use UTC methods so a UTC
  // midnight created by XLSX doesn't shift to the previous day in negative
  // UTC offsets.
  if (v instanceof Date && !isNaN(v.getTime())) {
    const y = v.getUTCFullYear();
    const m = String(v.getUTCMonth() + 1).padStart(2, "0");
    const d = String(v.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  // Numeric Excel serial (defensive fallback)
  if (typeof v === "number" && isFinite(v) && v > 59 && v < 200000) {
    try {
      const dc = XLSX.SSF.parse_date_code(v);
      if (dc && dc.y && dc.m && dc.d) {
        return `${dc.y}-${String(dc.m).padStart(2, "0")}-${String(dc.d).padStart(2, "0")}`;
      }
    } catch (_) { /* ignore */ }
  }

  const s = String(v).trim().split(" ")[0].split("T")[0];
  if (!s) return "";

  // ISO: 2026-07-24
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, "0")}-${iso[3].padStart(2, "0")}`;

  // TR with dots or dashes: 24.07.2026 / 24-07-2026 → always DD.MM.YYYY
  const tr = s.match(/^(\d{1,2})[.-](\d{1,2})[.-](\d{2,4})$/);
  if (tr) {
    const y = expandYear(tr[3]);
    return y ? `${y}-${tr[2].padStart(2, "0")}-${tr[1].padStart(2, "0")}` : "";
  }

  // Slash: assume TR (GG/AA/YYYY) by default; use disambiguation when possible
  const sl = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (sl) {
    const a = parseInt(sl[1], 10);
    const b = parseInt(sl[2], 10);
    const y = expandYear(sl[3]);
    if (!y) return "";
    let day, month;
    if (a > 12) { day = a; month = b; }        // a>12 → TR: DD/MM
    else if (b > 12) { day = b; month = a; }   // b>12 → US: MM/DD
    else { day = a; month = b; }               // TR default: DD/MM
    if (month < 1 || month > 12 || day < 1 || day > 31) return "";
    return `${y}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  return "";
}

// Turkish amount: '15592,80' / '15.592,80' / '15592.80' → number (NaN if invalid)
export function parseTRAmount(v) {
  if (v === null || v === undefined || v === "") return NaN;
  let s = String(v).trim();
  if (s.includes(",") && s.lastIndexOf(",") > s.lastIndexOf(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    s = s.replace(/,/g, "");
  }
  const n = parseFloat(s);
  return isNaN(n) ? NaN : n;
}
